import { v4 as uuid } from 'uuid';
import React from 'react';
import {
  FormBuilder_PrimitiveType,
  type FormBuilder_Event,
  type FormBuilder_Event_Field,
} from '~/graphql/types';
import JustificationContainer from '~/components/atom/JustificationContainer';

import Button from '~/components/atom/Button';
import styled, { css } from 'styled-components';
import { scrollBarStyles } from '~/components/molecule/OverflowScrollWrapper';
import type { EditProps } from '../..';
import { useRecoilState } from 'recoil';
import { interactionState } from '../../../../state';
import { eventById } from '../../../../state/nodesAndEvents';
import NoEventSelected from './components/NoEventSelected';
import EditableText from '~/components/organism/EditableText';
import FieldEditor from './components/FieldEditor';
import TipBanner from '~/components/organism/TipBanner';
import TextButton from '~/components/atom/TextButton';

export type Props = EditProps<any>;

const text = {
  explainEvents: `Ich höre schon des Dorfs Getümmel, Hier ist des Volkes wahrer Himmel, Zufrieden jauchzet groß und klein, Hier bin ich nicht; doch viel ist mir bewusst. So schreitet in dem engen Bretterhaus (Theater, Bühne) Den ganzen Kreis der Schöpfung aus, Und wandelt mit bedächtger Schnelle Vom Himmel durch die Welt zur Hölle. Gewöhnlich glaubt der Mensch, wenn er sie beim Kragen hätte.`,
  explainEventsHeader: 'Wat zijn gebeurtenissen?',
};

const EditEvent: React.FCC<Props> = () => {
  const [interaction, setInteraction] = useRecoilState(interactionState);
  const [event, setEvent] = useRecoilState(
    eventById(interaction?.subjectId ?? ''),
  );
  const eventId = interaction?.subjectId;

  if (!eventId) return <NoEventSelected />;

  const onDeleteField = (field: FormBuilder_Event_Field) => {
    setEvent((prev: FormBuilder_Event) => ({
      ...prev,
      fields: prev?.fields.filter(currField => currField.key !== field.key),
    }));
  };

  return (
    <Container padding={['xl']} width="100%" gap="m" direction="column">
      <EditableText
        as="h1"
        text={event?.name ?? 'Nieuw Event'}
        onSave={nextName => {
          setEvent(prev => {
            if (!prev) return prev;

            return { ...prev, name: nextName };
          });
        }}
      />
      <TipBanner
        headerText={text.explainEventsHeader}
        id="explain-events-event-editor"
      >
        {text.explainEvents}
      </TipBanner>
      <JustificationContainer
        direction="column"
        gap="l"
        height="100%"
        width="100%"
      >
        {event?.fields.map(field => (
          <FieldEditor
            key={field.key}
            field={field}
            eventId={eventId}
            onDelete={onDeleteField}
          />
        ))}

        <JustificationContainer margin={[null, null, 'xl', null]}>
          <Button
            label="Nieuw veld toevoegen"
            icon="plus"
            onClick={() => {
              setEvent(prev => {
                if (prev) {
                  return {
                    ...prev,
                    fields: [
                      ...prev.fields,
                      {
                        __typename: 'FormBuilder_Event_Field',
                        key: uuid(),
                        name: '',
                        type: {
                          __typename: 'FormBuilder_Event_Field_Type_Primitive',
                          type: FormBuilder_PrimitiveType.String,
                        },
                      },
                    ],
                  };
                }
                return prev;
              });
            }}
          />
        </JustificationContainer>
        <JustificationContainer
          align="center"
          justification="space-between"
          width="100%"
        >
          <TextButton
            padding={[null]}
            label="Annuleren"
            appearance="danger"
            onClick={() => setInteraction(null)}
          />
          <Button
            label="Opslaan en sluiten"
            appearance="secondary"
            onClick={() => setInteraction(null)}
          />
        </JustificationContainer>
      </JustificationContainer>
    </Container>
  );
};

const Container = styled(JustificationContainer)<{}>`
  ${({ theme }) => css`
    border-left: 1px solid ${theme.color('grey', 'light')};
    flex-shrink: 0;

    overflow-y: scroll;
    ${scrollBarStyles}
  `};
`;

export default EditEvent;
